'use client';
import { memo, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import MUIIconButton from '@mui/material/IconButton';
import MUIButton from '@core/design-system/button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FiDownload } from '@react-icons/all-files/fi/FiDownload';
import { useTheme } from '@mui/material';

import { SocialSharePopover } from '../social-share-popover';
import { AiOutlineLineChart } from '@react-icons/all-files/ai/AiOutlineLineChart';
import { FiLogOut } from '@react-icons/all-files/fi/FiLogOut';
import { FaWhatsapp } from '@react-icons/all-files/fa/FaWhatsapp';
import { GoFile } from '@react-icons/all-files/go/GoFile';
import { HighlightsText } from './highlights-text';
import { AssetHeaders } from '../asset-data/asset-headers';
import { MdCompareArrows } from '@react-icons/all-files/md/MdCompareArrows';
import { DEFAULT_ROUTES } from '@mais-retorno-front/constants/routes';
import { SponsoredInvestmentModal } from './sponsored-investment-modal';

export const SponsoredB3Header = memo<SponsoredProps>(
  ({
    nicename,
    handlerDownloadPDF,
    ctaContact,
    headers,
    managerWebsite,
    identifier,
    descriptionDocument,
    monthlyReport,
    investmentList,
  }) => {
    const router = useRouter();
    const theme = useTheme();

    return (
      <div>
        <HighlightsText label="Lâmina Patrocinada" />

        <Stack
          gap={2}
          sx={(theme) => ({
            p: 3,
            py: 6,
            borderRadius: 3,
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
          })}
        >
          <Stack
            flexDirection="row"
            gap={2}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Typography variant="h1" fontWeight={700} lineHeight={1.1}>
              {nicename}
            </Typography>

            <Stack flexDirection="row" gap={1}>
              <IconButton
                onClick={handlerDownloadPDF}
                aria-label="Baixar PDF"
                title="Baixar PDF"
              >
                <FiDownload />
              </IconButton>
              <SocialSharePopover
                tooltipText="Compartilhar Lâmina"
                url={`https://maisretorno.com${router.asPath}`}
                description="Dá uma olhada nesse ativo"
                style={{
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  margin: 0,
                }}
              />
            </Stack>
          </Stack>

          <Stack flexDirection="row" gap={1} flexWrap="wrap">
            {ctaContact && (
              <Button
                id="cta-link"
                target="_blank"
                rel="nofollow"
                component="a"
                variant="contained"
                title="Fale com a Gestora"
                href={ctaContact}
                endIcon={<FaWhatsapp />}
              >
                Falar com a gestora
              </Button>
            )}

            <div>
              <SponsoredInvestmentModal
                investmentList={investmentList}
                buttonProps={{ variant: 'outlined', sx: { color: '#fff' } }}
              />
            </div>

            <Button
              title="Compare esse ativo"
              component="a"
              LinkComponent={Link}
              prefetch={false}
              target="_blank"
              sx={{ color: '#fff' }}
              endIcon={<MdCompareArrows color={theme.palette.secondary.main} />}
              href={`${DEFAULT_ROUTES.assetsComparison}?p=otimo&a=${identifier}`}
            >
              Comparar
            </Button>

            <Button
              href={`${DEFAULT_ROUTES.createIncomeSimulation}?asset_identifier=${identifier}`}
              title="Crie uma simulação de carteira com esse fundo"
              endIcon={
                <AiOutlineLineChart color={theme.palette.secondary.main} />
              }
              sx={{ color: '#fff' }}
            >
              Simular
            </Button>

            {managerWebsite && (
              <Button
                target="_blank"
                rel="nofollow"
                component="a"
                href={managerWebsite}
                title="Acesse o site da gestora"
                endIcon={<FiLogOut color={theme.palette.secondary.main} />}
                sx={{ color: '#fff' }}
              >
                Site da gestora
              </Button>
            )}

            {Boolean(descriptionDocument) && (
              <Button
                target="_blank"
                component="a"
                href={descriptionDocument}
                LinkComponent={Link}
                endIcon={<GoFile color={theme.palette.secondary.main} />}
                sx={{ color: '#fff' }}
              >
                Baixar Lâmina do Fundo
              </Button>
            )}

            {Boolean(monthlyReport) && (
              <Button
                target="_blank"
                component="a"
                href={monthlyReport}
                endIcon={<GoFile color={theme.palette.secondary.main} />}
                sx={{ color: '#fff' }}
              >
                Baixar Relatório Mensal
              </Button>
            )}
          </Stack>

          <AssetHeaders
            headers={headers}
            downloadButton={false}
            headerStyle={{
              backgroundColor: '#ffffff26',
              '*': { color: '#fff' },
            }}
          />
        </Stack>
      </div>
    );
  }
);

const Button = (props: any) => (
  <MUIButton
    color="secondary"
    variant="outlined"
    {...props}
    sx={{
      textTransform: 'none',
      width: { xs: '100%', sm: 'auto' },
      ...props?.sx,
    }}
  >
    {props.children}
  </MUIButton>
);

const IconButton = ({ children, ...props }) => (
  <MUIIconButton
    color="primary"
    sx={{
      borderRadius: 2,
      backgroundColor: '#fff',
      '&: hover': { backgroundColor: '#e5e5e5' },
    }}
    {...props}
  >
    {children}
  </MUIIconButton>
);

interface SponsoredProps extends AssetHeaders {
  nicename: string;
  identifier: string;
  ctaContact: string | null;
  handlerDownloadPDF: () => void;
  managerWebsite?: string;
  descriptionDocument?: string;
  monthlyReport?: string;
  investmentList: {
    label: string;
    link: string;
  }[];
}
